import React, { FC, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Form as bootstrapForm, Button } from 'react-bootstrap';
import useCreateFormFromTemplate from '../../../hooks/api/form/useCreateFormFromTemplate';
import ModalComponent from '../../../components/modal/ModalComponent';
import Label from '../../../components/library/inputs/label/Label';
import SelectInput from '../../../components/library/inputs/select/SelectInput';
import useFormTemplates from '../../../hooks/api/form/useFormTemplates';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import * as Yup from 'yup';
import { Formik, Form, FormikProps } from 'formik';
import _ from 'lodash';
import { languages as availableLanguages } from '../../../utilities/CultureUtilities';
import Avatar from '../../../components/layout/avatar/Avatar';

interface Props {
  open: boolean;
  close: () => void;
}

interface Values {
  formName: string;
  languages: string[];
}

const CreateNewFormModal: FC<Props> = ({ open, close }: Props) => {
  const intl = useIntl();
  const formRef = useRef<FormikProps<Values>>(null);
  const [template, setTemplate] = useState<string | null>(null);
  const organizationId = useSelector((state: RootState) => state.organization.organization?.id) as string;
  const { formTemplates, isLoading: isLoadingTemplates, setOrganizationId } = useFormTemplates();
  const [avatar, setAvatar] = useState<File>();
  const [avatarUrl, setAvatarUrl] = useState<string | undefined>();
  const inputRef = useRef<HTMLInputElement>(null);

  const {
    createFormFromTemplate,
    isLoading: isLoadingForm,
    setOrganizationId: setId,
  } = useCreateFormFromTemplate(organizationId);

  useEffect(() => {
    if (organizationId) {
      setOrganizationId(organizationId);
      setId(organizationId);
    }
  }, [organizationId]);

  useEffect(() => {
    if (formTemplates && formTemplates.length > 0) {
      setTemplate(formTemplates[0].id);
    }
  }, [formTemplates]);

  const FormCreationErrorMessages = Yup.object().shape({
    formName: Yup.string()
      .min(1, intl.formatMessage({ id: 'formSettings.errorMessage.short' }))
      .max(100, intl.formatMessage({ id: 'formSettings.errorMessage.long' }))
      .required(intl.formatMessage({ id: 'formSettings.errorMessage.required' })),
    languages: Yup.array()
      .of(Yup.string())
      .min(1, intl.formatMessage({ id: 'formSettings.errorMessage.required' })),
  });

  const handleUpload = () => {
    inputRef.current?.click();
  };

  const handleAvatarChange = async (e) => {
    setAvatar(e.target.files[0]);
    const newAvatar = URL.createObjectURL(e.target.files[0]);
    setAvatarUrl(newAvatar);
  };

  if (template == null) return null;

  return (
    <ModalComponent
      closeSettings={close}
      buttons={[
        {
          text: intl.formatMessage({ id: 'cancel' }),
          buttonVariant: 'secondary',
          onClick: close,
        },
        {
          text: intl.formatMessage({ id: 'confirm' }),
          buttonVariant: 'primary',
          loading: isLoadingForm,
          onClick: () => {
            formRef.current?.submitForm();
          },
        },
      ]}
      show={open}
      modalHeading={intl.formatMessage({
        id: 'formList.createNewFormModal.header',
      })}
      modalSize="lg"
    >
      <div className="container px-5 pb-5">
        <Formik
          validationSchema={FormCreationErrorMessages}
          initialValues={{
            formName: '',
            languages: [intl.locale],
          }}
          onSubmit={(values: Values) => {
            if (values.formName.length > 0 && values.languages.length > 0) {
              createFormFromTemplate(values.formName, values.languages, template, avatar as File);
            }
          }}
          innerRef={formRef}
        >
          {({ errors, touched, values, handleChange, handleBlur, setFieldValue }) => {
            return (
              <Form>
                <bootstrapForm.Group className="d-flex flex-column justify-content-center align-items-center pt-0">
                  <div className="badge-container mb-3 position-relative" onClick={handleUpload}>
                    <Avatar src={avatarUrl} width={84} height={84}></Avatar>{' '}
                    <Button className="badge" variant="primary" size="sm">
                      <span className="mdi mdi-pencil">
                        <input ref={inputRef} onChange={handleAvatarChange} className="file-input d-none" type="file" />
                      </span>
                    </Button>
                  </div>
                  <Label label={intl.formatMessage({ id: 'formList.createNewFormModal.formName' })}></Label>
                  <bootstrapForm.Control
                    type="input"
                    name="formName"
                    placeholder={intl.formatMessage({ id: 'formList.createNewFormModal.formName' })}
                    isInvalid={touched.formName && !!errors.formName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.formName}
                  />
                  <bootstrapForm.Control.Feedback className="text-center" type="invalid">
                    {errors.formName && touched.formName && (
                      <FormattedMessage id="formList.createNewFormModal.emptyFormName"></FormattedMessage>
                    )}
                  </bootstrapForm.Control.Feedback>
                  {formTemplates && !isLoadingTemplates && (
                    <div className="d-flex flex-column justify-content-center align-items-center pt-4">
                      <Label label={intl.formatMessage({ id: 'formList.createNewFormModal.template' })} />
                      <SelectInput
                        value={template}
                        options={formTemplates.map((temp) => ({
                          text: intl.formatMessage({ id: `formList.templates.${temp.name}` }),
                          value: temp.id,
                        }))}
                        onChange={(e) => setTemplate(e.target.value)}
                        noEmptyValue
                      />
                    </div>
                  )}
                  <div className="d-flex flex-column justify-content-center align-items-center pt-4">
                    <Label label={'Languages'}></Label>
                    {availableLanguages.map((language) => {
                      const isSelected = _.includes(values.languages, language.locale);
                      return (
                        <div
                          className={`language d-flex justify-content-between align-items-center ${
                            isSelected ? 'selected' : ''
                          }`}
                          key={language.locale}
                          onClick={async () => {
                            if (isSelected) {
                              await setFieldValue(
                                'languages',
                                values.languages.filter((val) => val !== language.locale),
                                true,
                              );
                            } else {
                              await setFieldValue('languages', [...values.languages, language.locale], true);
                            }
                          }}
                        >
                          <span>{intl.formatMessage({ id: language.text })}</span>
                          <div className="icon-container">
                            {isSelected && (
                              <span className="icon">
                                <span className="mdi mdi-check-circle check-icon"></span>
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <bootstrapForm.Control.Feedback className="text-center" type="invalid">
                    {errors.languages && touched.languages && (
                      <FormattedMessage id="formList.createNewFormModal.emptyLanguage"></FormattedMessage>
                    )}
                  </bootstrapForm.Control.Feedback>
                </bootstrapForm.Group>
              </Form>
            );
          }}
        </Formik>
      </div>
    </ModalComponent>
  );
};

export default CreateNewFormModal;
